import axios, { AxiosError } from 'axios';
import { config } from '../utils/config';

// Error response interface
interface ApiErrorResponse {
  message?: string;
  error?: string;
  statusCode?: number;
  details?: any;
}

// Error handler function
const handleApiError = (error: AxiosError): Promise<never> => {
  const response = error.response;
  const request = error.request;

  // Network error (no response received)
  if (error.code === 'ECONNABORTED') {
    console.error('Request timeout:', error.message);
    return Promise.reject(new Error('Request timeout. Please try again.'));
  }

  if (!response && request) {
    console.error('Network error - no response received:', error.message);
    return Promise.reject(
      new Error('Network error. Please check your connection.'),
    );
  }

  // Response received but with error status
  if (response) {
    const { status, data } = response;
    const errorData = data as ApiErrorResponse;

    switch (status) {
      case 401:
        console.error('Unauthorized access - redirecting to login');
        // Handle authentication error - could redirect to login
        if (typeof window !== 'undefined') {
          window.location.href = config.auth.authUrl;
        }
        return Promise.reject(
          new Error('Session expired. Please log in again.'),
        );

      case 403:
        console.error(
          'Forbidden access:',
          errorData.message || 'Access denied',
        );
        return Promise.reject(
          new Error('You do not have permission to perform this action.'),
        );

      case 404:
        console.error('Resource not found:', errorData.message || 'Not found');
        return Promise.reject(
          new Error('The requested resource was not found.'),
        );

      case 422:
        console.error(
          'Validation error:',
          errorData.message || errorData.details,
        );
        return Promise.reject(
          new Error(errorData.message || 'Validation error occurred.'),
        );

      case 429:
        console.error('Rate limit exceeded');
        return Promise.reject(
          new Error('Too many requests. Please try again later.'),
        );

      case 500:
        console.error('Internal server error:', errorData.message);
        return Promise.reject(
          new Error('Internal server error. Please try again later.'),
        );

      case 502:
      case 503:
      case 504:
        console.error('Service unavailable:', status);
        return Promise.reject(
          new Error('Service temporarily unavailable. Please try again later.'),
        );

      default:
        console.error(
          `API Error ${status}:`,
          errorData.message || error.message,
        );
        return Promise.reject(
          new Error(errorData.message || 'An unexpected error occurred.'),
        );
    }
  }

  // Generic error fallback
  console.error('Unknown error:', error.message);
  return Promise.reject(new Error('An unexpected error occurred.'));
};

// Choice API client for customization, audit, analytics, builder, etc.
export const choiceApiClient = axios.create({
  baseURL: config.service.choice.host,
  timeout: 10000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Add error interceptor to choiceApiClient
choiceApiClient.interceptors.response.use(
  (response) => response,
  handleApiError,
);

// CMP client for tag-related services and content
export const cmpClient = axios.create({
  baseURL: config.service.cmp.host,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Add error interceptor to cmpClient
cmpClient.interceptors.response.use((response) => response, handleApiError);

// CMP API client for version lists and customized banner links
export const cmpApiClient = axios.create({
  baseURL: config.service.cmpApi.host,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add error interceptor to cmpApiClient
cmpApiClient.interceptors.response.use((response) => response, handleApiError);
